import React, { useState } from 'react';
import { useGetKYBResultsByPaymentStatusQuery } from '../app/api';
import PropTypes from 'prop-types';
import {Button, OverlayTrigger, Popover, Pagination} from 'react-bootstrap';
import { BarChart3, Search, ChevronUp, ChevronDown } from 'lucide-react';

export default function Status() {
  
  const [filters, setFilters] = useState({
    payStatus: 'all',
    kybStatus: 'all',
    kycStatus: 'all',
    legalStatus: 'all',
    accountType: 'all',
    assignedTo: 'all',
    submissionSource: 'all'
  });

  const [sorting, setSorting] = useState({
    field: null,
    direction: 'asc'
  });
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25
  });

  const kybStatuses = [
    { value: 'all', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'KYB_APPROVED_KYC_BLOCK', label: 'KYC Blocked' },
    { value: 'DENIED', label: 'Denied' }
  ];

  const kycStatuses = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'complete', label: 'Complete' },
    { value: 'denied', label: 'Denied' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'aml-blocked', label: 'AML Blocked' },
    { value: 'expired', label: 'Expired' },
    { value: 'unknown', label: 'Unknown' }
  ];

  const paymentStatuses = [
    { value: 'all', label: 'All' },
    { value: 'UNKNOWN', label: 'Unknown' },
    { value: 'PENDING', label: 'Pending Approval' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'DENIED', label: 'Denied' }
  ];

  const legalStatuses = [
    { value: 'all', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'LEGAL_OVERRIDE_APPROVED', label: 'Legal Override Approved' },
    { value: 'DENIED', label: 'Denied' },
    { value: 'EXPIRED', label: 'Expired' }
  ];
  
  const accountTypes = [
    { value: 'all', label: 'All' },
    { value: 'studio', label: 'Studio' },
    { value: 'partner', label: 'Affiliate' },
    { value: 'influencer', label: 'Influencer' }
  ];
  
  const assignmentTypes = [
    { value: 'all', label: 'All Departments' },
    { value: 'Affiliates', label: 'Affiliates' },
    { value: 'Studio/Models', label: 'Studio/Models' },
    { value: 'Influencers', label: 'Influencers' },
    { value: 'Accounting', label: 'Accounting' },
    { value: 'Legal', label: 'Legal' },
    { value: 'unassigned', label: 'Unassigned' }
  ];

  const submissionSourceTypes = [
    { value: 'all', label: 'All Sources' },
    { value: 'self_submitted', label: 'Self-Submitted' },
    { value: 'external', label: 'External/Admin-Sent' }
  ];

  const { data = {}, isLoading: loading } = useGetKYBResultsByPaymentStatusQuery({
    assigned_to: filters.assignedTo === 'all' ? null : filters.assignedTo,
    pay_status: filters.payStatus === 'all' ? null : filters.payStatus,
    kyb_status: filters.kybStatus === 'all' ? null : filters.kybStatus,
    kyc_status: filters.kycStatus === 'all' ? null : filters.kycStatus,
    due_diligence_status: filters.legalStatus === 'all' ? null : filters.legalStatus,
    account_type: filters.accountType === 'all' ? null : filters.accountType,
    submission_source: filters.submissionSource === 'all' ? null : filters.submissionSource,
    page: pagination.currentPage,
    page_size: pagination.pageSize
  });
  
  const { results: allResults = [], total = 0, page = 1, page_size = 25, total_pages = 1, filter_counts = {} } = data;

  const statusCounts = filter_counts || {
    payment: {},
    kyb: {},
    kyc: {},
    legal: {},
    assigned: {},
    account_type: {},
    submission_source: {}
  };

  const getFilterCount = (category, value) => {
    if (value === 'all') {
      return Object.values(statusCounts?.[category] || {}).reduce((sum, val) => sum + val, 0);
    }
    return statusCounts?.[category]?.[value] || 0;
  };

  const filteredResults = allResults;

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const statusPriorities = {
    kyb_status: {
      'PENDING': 1,
      'SUBMITTED': 2,
      'APPROVED': 3,
      'KYB_APPROVED_KYC_BLOCK': 4,
      'DENIED': 5
    },
    kyc_status: {
      'not-initiated': 1,
      'pending': 2,
      'internal-review': 3,
      'complete': 4,
      'denied': 5,
      'blocked': 6,
      'aml-blocked': 7,
      'expired': 8,
      'unknown': 9
    },
    due_diligence_status: {
      'PENDING': 1,
      'APPROVED': 2,
      'LEGAL_OVERRIDE_APPROVED': 3,
      'EXPIRED': 4,
      'DENIED': 5
    },
    pay_status: {
      'UNKNOWN': 1,
      'PENDING': 2,
      'APPROVED': 3,
      'DENIED': 4
    }
  };

  const sortedResults = React.useMemo(() => {
    if (!sorting.field) return filteredResults;

    return [...filteredResults].sort((a, b) => {
      let aValue = a[sorting.field];
      let bValue = b[sorting.field];

      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (sorting.field === 'submit_timestamp') {
        aValue = new Date(a[sorting.field] || 0);
        bValue = new Date(b[sorting.field] || 0);
      }
      else if (statusPriorities[sorting.field]) {
        const priorities = statusPriorities[sorting.field];
        aValue = priorities[aValue] || 999; // Unknown statuses go to end
        bValue = priorities[bValue] || 999;
      }
      else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) return sorting.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredResults, sorting]);

  const SortableHeader = ({ field, children, className = "" }) => {
    const getSortIcon = () => {
      if (!field) return null;
      
      if (sorting.field === field) {
        return sorting.direction === 'asc' ? 
          <ChevronUp size={16} className="text-primary ms-2" /> : 
          <ChevronDown size={16} className="text-primary ms-2" />;
      }
      
      return (
        <div className="d-flex flex-column ms-2" style={{ gap: '1px' }}>
          <ChevronUp size={12} className="text-muted opacity-50" />
          <ChevronDown size={12} className="text-muted opacity-50" style={{ marginTop: '-4px' }} />
        </div>
      );
    };

    return (
      <th 
        className={`border-0 py-3 ${className} ${field ? 'user-select-none' : ''}`}
        style={{ 
          cursor: field ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
          fontWeight: '600',
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: '#6c757d'
        }}
        onClick={() => field && handleSort(field)}
      >
        <div className="d-flex align-items-center justify-content-center">
          <span>{children}</span>
          {getSortIcon()}
        </div>
      </th>
    );
  };

  SortableHeader.propTypes = {
    field: PropTypes.string,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badgeClasses = {
      'APPROVED': 'bg-success',
      'DENIED': 'bg-danger',
      'SUBMITTED': 'bg-warning text-dark',
      'PENDING': 'bg-warning text-dark',
      'UNKNOWN': 'bg-secondary',
      'PENDING APPROVAL': 'bg-warning text-dark',
      'complete': 'bg-success',
      'pending': 'bg-warning text-dark',
      'blocked': 'bg-danger',
      'aml-blocked': 'bg-danger',
      'internal-review': 'bg-warning text-dark',
      'not-initiated': 'bg-secondary',
      'expired': 'bg-danger',
      'unknown': 'bg-secondary',
      null: 'bg-secondary'
    };
    
    const badgeClass = badgeClasses[status] || 'bg-secondary';
    const displayText = status || 'N/A';
    
    return (
      <span className={`badge ${badgeClass}`}>
        {displayText}
      </span>
    );
  };

  const StatusButton = ({ status, count, isActive, onClick, type }) => (
    <button
      className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-primary'}`}
      onClick={() => onClick(status.value, type)}
    >
      {status.label}
      {status.value !== 'all' && <span className="badge bg-light text-dark ms-2">{count}</span>}
    </button>
  );

  StatusButton.propTypes = {
    status: PropTypes.object.isRequired,
    count: PropTypes.number.isRequired,
    isActive: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
  };

  const handleFilterChange = (statusValue, filterType) => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    
    setFilters(prev => ({
      ...prev,
      [filterType]: statusValue
    }));
  };

  const clearAllFilters = () => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    
    setFilters({
      payStatus: 'all',
      kybStatus: 'all',
      kycStatus: 'all',
      legalStatus: 'all',
      accountType: 'all',
      assignedTo: 'all',
      submissionSource: 'all'
    });
  };

  const handleViewDetails = (result) => {
    const accountType = result.account_type === 'partner' ? 'affiliate' : result.account_type || 'unknown';
    const accountId = result.account_id;
    
    window.open(`/cs/${accountType}/${accountId}`, '_blank');
  };

  const getActiveFiltersText = () => {
    const activeFilters = [];
    if (filters.accountType !== 'all') {
      const accountTypeLabel = accountTypes.find(type => type.value === filters.accountType)?.label || filters.accountType;
      activeFilters.push(`Type: ${accountTypeLabel}`);
    }
    if (filters.payStatus !== 'all') {
      activeFilters.push(`Payment: ${filters.payStatus}`);
    }
    if (filters.kybStatus !== 'all') {
      activeFilters.push(`KYB: ${filters.kybStatus}`);
    }
    if (filters.kycStatus !== 'all') {
      activeFilters.push(`KYC: ${filters.kycStatus}`);
    }
    if (filters.legalStatus !== 'all') {
      activeFilters.push(`Legal: ${filters.legalStatus}`);
    }
    if (filters.assignedTo !== 'all') {
      const assignedToLabel = assignmentTypes.find(type => type.value === filters.assignedTo)?.label || filters.assignedTo;
      activeFilters.push(`Department: ${assignedToLabel}`);
    }
    if (filters.submissionSource !== 'all') {
      const sourceLabel = submissionSourceTypes.find(type => type.value === filters.submissionSource)?.label || filters.submissionSource;
      activeFilters.push(`Source: ${sourceLabel}`);
    }
    return activeFilters.length > 0 ? activeFilters.join(' | ') : 'No filters applied';
  };

  const hasActiveFilters = () => {
    return filters.accountType !== 'all' || 
           filters.payStatus !== 'all' || 
           filters.kybStatus !== 'all' || 
           filters.kycStatus !== 'all' || 
           filters.legalStatus !== 'all' || 
           filters.assignedTo !== 'all' ||
           filters.submissionSource !== 'all';
  };

  const effectiveTotal = total;
  const totalPages = total_pages;

  const renderNoteWithMentions = (text) => {
    if (!text) return null;
    const mentionRegex = /@([^%]+)%[^%]*%/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      parts.push(
        <span key={match.index}>
          @{match[1]}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  return (
    <>
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet"
      />
      
      <div className="min-vh-100" style={{ 
        background: 'linear-gradient(135deg, #e3f2fd 0%, #e3f2fd 100%)',
        padding: '2rem 0'
      }}>
        <div className="container">
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold text-dark mb-3 d-flex align-items-center justify-content-center">
              <BarChart3 className="me-3" size={56} />
              Status Dashboard
            </h1>
            <p className="fs-5 text-muted mb-0">Monitor and manage account status across all verification stages</p>
          </div>

                     <div className="card shadow-lg border-0 mb-5" style={{ borderRadius: '1rem' }}>
             <div className="card-header bg-white border-0 py-4">
               <h2 className="h4 fw-semibold mb-0">Filter by Status</h2>
             </div>
             <div className="card-body px-4 py-4">
              <div className="row g-3">
                <div className="col-12 col-md">
                  <label className="form-label fw-semibold text-secondary text-uppercase mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Account Type</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.accountType}
                    onChange={(e) => handleFilterChange(e.target.value, 'accountType')}
                  >
                    {accountTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} ({getFilterCount('account_type', type.value)})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md">
                  <label className="form-label fw-semibold text-secondary text-uppercase mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Assigned To</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.assignedTo}
                    onChange={(e) => handleFilterChange(e.target.value, 'assignedTo')}
                  >
                    {assignmentTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} ({getFilterCount('assigned', type.value)})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="col-12 col-md">
                  <label className="form-label fw-semibold text-secondary text-uppercase mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>KYB Status</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.kybStatus}
                    onChange={(e) => handleFilterChange(e.target.value, 'kybStatus')}
                  >
                    {kybStatuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label} ({getFilterCount('kyb', status.value)})
                      </option>
                    ))}
                  </select>
               </div>
               
                <div className="col-12 col-md">
                  <label className="form-label fw-semibold text-secondary text-uppercase mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>KYC Status</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.kycStatus}
                    onChange={(e) => handleFilterChange(e.target.value, 'kycStatus')}
                  >
                    {kycStatuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label} ({getFilterCount('kyc', status.value)})
                      </option>
                    ))}
                  </select>
               </div>
               
                <div className="col-12 col-md">
                  <label className="form-label fw-semibold text-secondary text-uppercase mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Legal Status</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.legalStatus}
                    onChange={(e) => handleFilterChange(e.target.value, 'legalStatus')}
                  >
                    {legalStatuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label} ({getFilterCount('legal', status.value)})
                      </option>
                    ))}
                  </select>
               </div>
               
                <div className="col-12 col-md">
                  <label className="form-label fw-semibold text-secondary text-uppercase mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Payment Status</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.payStatus}
                    onChange={(e) => handleFilterChange(e.target.value, 'payStatus')}
                  >
                    {paymentStatuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label} ({getFilterCount('payment', status.value)})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md">
                  <label className="form-label fw-semibold text-secondary text-uppercase mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>Submission Source</label>
                  <select 
                    className="form-select form-select-sm"
                    value={filters.submissionSource}
                    onChange={(e) => handleFilterChange(e.target.value, 'submissionSource')}
                  >
                    {submissionSourceTypes.map(source => (
                      <option key={source.value} value={source.value}>
                        {source.label} ({getFilterCount('submission_source', source.value)})
                      </option>
                    ))}
                  </select>
                </div>
                
              </div>
            </div>
          </div>

          <div className="card shadow-lg border-0" style={{ borderRadius: '1rem' }}>
                         <div className="card-header bg-white border-0 py-4">
               <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <h2 className="h4 fw-semibold mb-0">Accounts</h2>
                  <span className="badge bg-secondary rounded-pill">{effectiveTotal} found</span>
                </div>
                 <div className="d-flex align-items-center">
                   <span className="text-muted me-3" style={{ fontSize: '0.875rem' }}>{getActiveFiltersText()}</span>
                   {hasActiveFilters() && (
                     <Button variant="outline-secondary" size="sm" onClick={clearAllFilters}>
                       Clear Filters
                     </Button>
                   )}
                 </div>
               </div>
             </div>
            
                         {sortedResults.length === 0 ? (
               <div className="card-body text-center py-5">
                 <Search size={48} className="text-muted mb-3" />
                 <p className="text-muted">No accounts found matching your criteria.</p>
                 {hasActiveFilters() && (
                   <Button variant="outline-primary" onClick={clearAllFilters}>
                     Clear All Filters
                   </Button>
                 )}
               </div>
             ) : (
               <div className="table-responsive" style={{ borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem', overflow: 'hidden' }}>
                 <table className="table table-hover table-nowrap mb-0">
                   <thead className="table-light">
                     <tr>
                       <SortableHeader field="account_type" className="ps-4">Type</SortableHeader>
                       <SortableHeader field="account_id">ID</SortableHeader>
                       <SortableHeader field="submit_timestamp">Submitted</SortableHeader>
                       <SortableHeader field="company_name">Company</SortableHeader>
                       <SortableHeader field="kyb_status" className="text-center">KYB</SortableHeader>
                       <SortableHeader field="kyc_status" className="text-center">KYC</SortableHeader>
                       <SortableHeader field="due_diligence_status" className="text-center">Legal</SortableHeader>
                       <SortableHeader field="pay_status" className="text-center">Payment</SortableHeader>
                       <SortableHeader className="text-center pe-4">Note</SortableHeader>
                     </tr>
                   </thead>
                   <tbody>
                     {loading ? (
                       <tr><td colSpan="9" className="text-center py-5">
                         <div className="spinner-border text-primary" role="status">
                           <span className="visually-hidden">Loading...</span>
                         </div>
                       </td></tr>
                     ) : (
                      sortedResults.map(result => (
                         <tr key={result.id} onClick={() => handleViewDetails(result)} 
                             style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                             onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                             onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                           <td className="py-3 ps-4">
                             <span className="fw-medium">
                              {result.account_type === 'partner' ? 'Affiliate' : 
                               !result.account_type?.trim() ? 'Unknown' : 
                               result.account_type?.charAt(0).toUpperCase() + result.account_type?.slice(1)}
                             </span>
                           </td>
                           <td className="text-muted py-3">{result.account_id}</td>
                           <td className="text-muted py-3">{formatDate(result.submit_timestamp)}</td>
                           <td className="fw-medium py-3">{result.company_name}</td>
                           <td className='text-center py-3'>{getStatusBadge(result.kyb_status)}</td>
                           <td className='text-center py-3'>{getStatusBadge(result.kyc_status)}</td>
                           <td className='text-center py-3'>{getStatusBadge(result.due_diligence_status)}</td>
                           <td className='text-center py-3'>{getStatusBadge(result.pay_status)}</td>
                           <td className='text-center py-3 pe-4'>
                             {result.featured_note ? (
                               <OverlayTrigger
                                 trigger={['hover', 'focus']}
                                 rootClose
                                 placement="top"
                                 overlay={
                                   <Popover id={`popover-top-${result.id}`}>
                                     <Popover.Body>
                                       {renderNoteWithMentions(result.featured_note)}
                                     </Popover.Body>
                                   </Popover>
                                 }
                               >
                                 <span 
                                   style={{
                                     cursor: 'pointer',
                                     display: 'inline-block',
                                     maxWidth: '200px',
                                     whiteSpace: 'nowrap',
                                     overflow: 'hidden',
                                     textOverflow: 'ellipsis'
                                   }}
                                 >
                                   {renderNoteWithMentions(result.featured_note)}
                                 </span>
                               </OverlayTrigger>
                             ) : (
                               <span className="text-muted">—</span>
                             )}
                           </td>
                         </tr>
                       ))
                     )}
                   </tbody>
                 </table>
              </div>
            )}

            {/* Pagination Controls */}
            {!loading && sortedResults.length > 0 && (
              <div className="d-flex justify-content-between align-items-center bg-white p-3 border-top">
                <div className="d-flex align-items-center">
                  <span className="me-3">
                    Showing {((page - 1) * page_size) + 1}-{Math.min(page * page_size, effectiveTotal)} of {effectiveTotal} records
                  </span>
                  <div className="d-flex align-items-center">
                    <span className="me-2">Items per page:</span>
                    <select 
                      className="form-select form-select-sm" 
                      style={{width: 'auto'}}
                      value={pagination.pageSize}
                        onChange={(e) => {
                          setPagination({
                            currentPage: 1,
                            pageSize: Number(e.target.value)
                          });
                        }}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <Pagination className="mb-0">
                  <Pagination.First 
                    onClick={() => setPagination({...pagination, currentPage: 1})}
                    disabled={page <= 1} 
                  />
                  <Pagination.Prev 
                    onClick={() => setPagination({...pagination, currentPage: page - 1})}
                    disabled={page <= 1} 
                  />
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    
                    return (
                      <Pagination.Item 
                        key={pageNum} 
                        active={pageNum === page}
                        onClick={() => setPagination({...pagination, currentPage: pageNum})}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  
                  <Pagination.Next 
                    onClick={() => setPagination({...pagination, currentPage: page + 1})}
                    disabled={page >= totalPages} 
                  />
                  <Pagination.Last 
                    onClick={() => setPagination({...pagination, currentPage: totalPages})}
                    disabled={page >= totalPages} 
                  />
                </Pagination>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js"></script>
    </>
  );
} 
